import React, { memo, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  Sankey,
  Scatter,
  ScatterChart,
  Tooltip,
  Treemap,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import ChatPager, { usePaged, BLOCK_TITLE_STYLE } from "shared/components/chat/ChatPager";
import { cellDisplay } from "shared/components/chat/chatCellFormat";
import { buildRechartsReadySpec } from "shared/types/trodoChartSpec";
import {
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableDataCellClass,
} from "features/activity/styles/dataTableStyles";

// Rows here open nothing: the table row without the pointer and hover.
const STATIC_ROW_CLASS = "h-8 border-b border-[color:var(--border-subtle)] last:border-b-0";

// The series colours are the brand's own tokens, so every chart reads right
// in light and dark mode: brand orange first (a one-series chart is Trodo's),
// then the feedback and accent hues, then the same hues mixed toward the
// page's surface for the eighth series onwards. Nothing here is a hex of its own.
const mixed = (token) => `color-mix(in srgb, var(${token}) 55%, var(--surface-base))`;
const DEFAULT_COLORS = [
  "var(--brand-primary)",
  "var(--feedback-info)",
  "var(--feedback-success)",
  "var(--accent-llm)",
  "var(--accent-enterprise)",
  "var(--feedback-warning)",
  mixed("--brand-primary"),
  mixed("--feedback-info"),
  mixed("--feedback-success"),
  mixed("--accent-llm"),
  "var(--feedback-error)", // red (last-resort accent)
  "var(--text-muted)", // slate (low-priority series)
];

function pickColor(seriesIndex, explicit) {
  if (typeof explicit === "string" && explicit.trim()) return explicit;
  return DEFAULT_COLORS[seriesIndex % DEFAULT_COLORS.length];
}

const gridStroke = theme.border;
const axisTick = { fill: theme.textSecondary, fontSize: 11 };

// ---------------------------------------------------------------------------
// Tick formatters
// ---------------------------------------------------------------------------

const _ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}([T\s]|$)/;

/** Detects if an x-value is an ISO date / timestamp and returns a short label. */
function formatPossibleDate(v) {
  if (typeof v !== "string") return null;
  if (!_ISO_DATE_RE.test(v)) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  // Compact format: "Apr 28" or "Apr 28 14:00" if time is meaningful
  const month = d.toLocaleString("en-US", { month: "short" });
  const day = d.getDate();
  return `${month} ${day}`;
}

/**
 * A number with its unit on it.
 *
 * The sub-1 branch used to assume every small number was a rate and round to
 * two decimals. On a cost chart that turned $0.003647 into "0.00" and the
 * table cell into "0" — telling the reader that model was free when it was
 * merely cheap. Money keeps enough decimals to stay non-zero; a rate still
 * gets two, because a rate of 0.00 IS approximately zero.
 */
function formatNumberTick(n, unit, maxAbs) {
  if (typeof n !== "number" || !Number.isFinite(n)) return n;
  const a = Math.abs(n);

  if (unit === "currency") {
    if (a === 0) return "$0";
    if (a >= 1_000) return "$" + (n / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
    if (a >= 1) return "$" + n.toFixed(2);
    // Below a dollar, show enough places that a real cost never reads as zero.
    if (a >= 0.01) return "$" + n.toFixed(3);
    return "$" + n.toFixed(6).replace(/0+$/, "").replace(/\.$/, "");
  }
  if (unit === "percent") return `${Math.round(n * 100) / 100}%`;
  if (unit === "duration_ms") {
    // ONE UNIT FOR THE WHOLE AXIS, chosen from the largest value on it.
    //
    // The ladder used to stop at seconds, so a chart of daily p99 over two
    // weeks read "0ms, 2500s, 5000s, 7500s, 10000s" — two units on one axis,
    // and four numbers nobody converts in their head, under prose that said
    // "133m 20s". Choosing per TICK instead only trades that for "41.7m" next
    // to "1.4h". The axis has one scale, so it gets one unit; `maxAbs` is that
    // scale, and without it each tick still falls back to its own magnitude.
    if (a === 0) return "0";
    const scale = Number.isFinite(maxAbs) && maxAbs > 0 ? maxAbs : a;
    if (scale < 1_000) return `${Math.round(n)}ms`;
    if (scale < 60_000) return `${Math.round(n / 100) / 10}s`;
    if (scale < 3_600_000) return `${Math.round(n / 6_000) / 10}m`;
    return `${Math.round(n / 360_000) / 10}h`;
  }

  if (a >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
  if (a >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (a >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  if (a > 0 && a < 1) {
    // Likely a rate / probability — show 2 decimals
    return n.toFixed(2);
  }
  // A SQL card's own number has no unit: 104.455624 reads as 104.46.
  return String(Number(n.toFixed(2)));
}

/**
 * The Y-axis formatter for a spec, bound to the largest value it will plot.
 *
 * Recharts hands a tickFormatter one value at a time, so without this every
 * tick picks its own unit and a duration axis mixes minutes with hours.
 */
function yTickFormatter(spec, rows) {
  const keys = (spec.series || []).map((sr) => sr.dataKey);
  let max = 0;
  for (const row of rows || []) {
    for (const k of keys) {
      const v = Number(row[k]);
      if (Number.isFinite(v)) max = Math.max(max, Math.abs(v));
    }
  }
  return (v) => formatNumberTick(v, spec.unit, max);
}

function formatXAxisTick(v) {
  const dateLabel = formatPossibleDate(v);
  if (dateLabel) return dateLabel;
  // 18 was chosen for a wider canvas than a chat bubble gives. A model name
  // truncated to 16 still overlapped its neighbour at 660px, so the budget is
  // tighter here — enough to tell `gpt-5.4-mini` from `gpt-5.4-mini-2026-03-17`
  // while the tooltip and the Table view carry the full value.
  if (typeof v === "string" && v.length > 14) return `${v.slice(0, 13)}…`;
  return String(v);
}

/** True if a column of x-values looks like dates/timestamps. */
function looksLikeTimeSeriesXAxis(data, xKey) {
  if (!Array.isArray(data) || data.length === 0) return false;
  let hits = 0;
  for (const row of data) {
    if (typeof row?.[xKey] === "string" && _ISO_DATE_RE.test(row[xKey])) hits++;
  }
  // ≥ 70% of rows are date-shaped → treat axis as time
  return hits / data.length >= 0.7;
}

// ---------------------------------------------------------------------------
// ChartBody — switches on kind, dispatches to one of bar / line / area / pie /
// funnel / scatter / heatmap / sankey / retention_matrix. Each branch is
// independent so quirks of one don't bleed into others.
// ---------------------------------------------------------------------------

let measureCtx = null;
/** Rendered width of an axis label (11px Inter), in px. */
function textWidth(t) {
  if (!measureCtx && typeof document !== "undefined") {
    try { measureCtx = document.createElement("canvas").getContext("2d"); } catch (_) { measureCtx = null; }
  }
  if (!measureCtx) return t.length * 7;
  measureCtx.font = "11px Inter, sans-serif";
  return measureCtx.measureText(t).width;
}

/**
 * A label shortened in the MIDDLE to fit `px`: names share their start
 * ("v3 suite · Run · …") and differ at the end, so the end must survive.
 */
function middleClip(t, px) {
  if (textWidth(t) <= px) return t;
  let keep = t.length - 1;
  while (keep > 2) {
    const head = Math.ceil(keep * 0.4);
    const out = `${t.slice(0, head)}…${t.slice(t.length - (keep - head))}`;
    if (textWidth(out) <= px) return out;
    keep -= 1;
  }
  return `${t.slice(0, 1)}…`;
}

/**
 * Every chart's hover, from the app's own tokens: the raised surface, its
 * border and shadow, the text colours, and a swatch in the series' colour.
 * Recharts' default draws a white box and paints a value with no colour of
 * its own (a sankey link, a treemap cell) #000 — black on a dark page.
 */
function BrandTooltip({ active, payload, label, format = (v) => v, labelFormat }) {
  if (!active || !payload || !payload.length) return null;
  const head = label != null && label !== "" ? (labelFormat ? labelFormat(label) : label) : null;
  return (
    <div style={{
      backgroundColor: "var(--surface-raised)", border: "1px solid var(--border-default)", borderRadius: 8,
      boxShadow: "var(--shadow-lg)", padding: "8px 12px", fontSize: 12, color: "var(--text-primary)", maxWidth: 320,
    }}>
      {head != null ? <div style={{ color: "var(--text-muted)", fontSize: 11, marginBottom: 4 }}>{String(head)}</div> : null}
      {payload.map((p, i) => {
        const swatch = p.color || p.payload?.fill || p.fill || "var(--text-muted)";
        const name = p.name ?? p.payload?.name ?? "";
        return (
          // eslint-disable-next-line react/no-array-index-key -- the hover's own order
          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "1px 0" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-secondary)", minWidth: 0 }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: swatch, flexShrink: 0 }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{String(name)}</span>
            </span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{format(p.value, p.name)}</span>
          </div>
        );
      })}
    </div>
  );
}
const HOVER_CURSOR = { fill: "var(--surface-hover)", opacity: 0.6 };

/**
 * A 100% stack: each row's series as its share of the row, 0–100, so the
 * axis reads 0%–100% whatever the measure was.
 */
function toPercentSpec(spec) {
  const keys = spec.series.map((s) => s.dataKey);
  const data = spec.data.map((row) => {
    const total = keys.reduce((t, k) => t + (Number(row[k]) > 0 ? Number(row[k]) : 0), 0);
    const out = { ...row };
    for (const k of keys) out[k] = total > 0 && row[k] != null ? (Math.max(0, Number(row[k])) / total) * 100 : null;
    return out;
  });
  return { ...spec, data, unit: "percent" };
}

/** A value whose neighbours on both sides are missing: a line cannot show it. */
function isolatedPoint(data, key, i) {
  const v = (j) => { const x = data[j] && data[j][key]; return x != null && Number.isFinite(Number(x)); };
  // Lines connect across empty buckets, so a point is alone only when it is
  // the series' only value.
  return v(i) && !data.some((_, j) => j !== i && v(j));
}

const ChartBody = memo(function ChartBody({ spec: given, width, height }) {
  const spec = useMemo(() => (given.percent ? toPercentSpec(given) : given), [given]);
  const { data, xKey, kind } = spec;
  const w = Math.max(120, Math.floor(width));
  const h = Math.max(80, Math.floor(height));


  // ---- pie / donut: single series, name=xKey, value=series[0].dataKey ----
  if (kind === "pie" || kind === "donut") {
    const sliceKey = spec.series[0]?.dataKey;
    if (!sliceKey) return null;
    const pieData = data.map((row, i) => ({
      name: String(row[xKey]),
      value: Number(row[sliceKey]) || 0,
      _i: i,
    }));
    // Room for the slice labels above and beside the ring.
    const outerRadius = Math.max(36, Math.min(w, h) / 2 - (w >= 440 ? 40 : 24));
    const innerRadius = kind === "donut" ? Math.max(20, Math.floor(outerRadius * 0.55)) : 0;
    return (
      <PieChart width={w} height={h}>
        <Pie
          data={pieData}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          paddingAngle={2}
          // Only slices big enough to hold a label get one; the legend and
          // the tooltip name the rest instead of a pile of overlapping text.
          label={w >= 440 ? (entry) => (entry.percent >= 0.05 ? `${middleClip(String(entry.name), 120)} (${formatNumberTick(entry.value, spec.unit)})` : null) : false}
          labelLine={false}
        >
          {pieData.map((entry) => (
            <Cell key={`slice-${entry._i}`} fill={pickColor(entry._i)} />
          ))}
        </Pie>
        <Tooltip wrapperStyle={{ outline: "none" }} content={<BrandTooltip format={(v) => formatNumberTick(v, spec.unit)} />} />
        <Legend wrapperStyle={{ fontSize: 12, color: theme.textSecondary, paddingTop: 4 }} />
      </PieChart>
    );
  }

  // ---- treemap: each value a rectangle sized by its share ----
  if (kind === "treemap") {
    const sizeKey = spec.series[0]?.dataKey;
    if (!sizeKey) return null;
    const cells = data
      .map((row, i) => ({ name: String(row[xKey]), size: Math.max(0, Number(row[sizeKey]) || 0), _i: i }))
      .filter((c) => c.size > 0);
    const Cellx = ({ x, y, width: cw, height: ch, name, size, _i: idx }) => (
      <g>
        <rect x={x} y={y} width={cw} height={ch} rx={3} fill={pickColor(idx ?? 0)} fillOpacity={0.85} stroke="var(--surface-base)" strokeWidth={2} />
        {cw > 56 && ch > 28 ? (
          <text x={x + 6} y={y + 16} fontSize={11} fill="var(--surface-base)" fontWeight={600}>
            {middleClip(String(name), cw - 12)}
            <tspan x={x + 6} dy={14} fontWeight={400}>{formatNumberTick(size, spec.unit)}</tspan>
          </text>
        ) : null}
      </g>
    );
    return (
      <Treemap width={w} height={h} data={cells} dataKey="size" nameKey="name" isAnimationActive={false} content={<Cellx />}>
        <Tooltip wrapperStyle={{ outline: "none" }} content={<BrandTooltip format={(v) => formatNumberTick(v, spec.unit)} />} />
      </Treemap>
    );
  }

  // ---- radar: each series a shape over the categories ----
  if (kind === "radar") {
    return (
      <RadarChart width={w} height={h} data={data} outerRadius={Math.max(30, Math.min(w, h) / 2 - 36)}>
        <PolarGrid stroke={gridStroke} />
        <PolarAngleAxis dataKey={xKey} tick={axisTick} tickFormatter={(v) => middleClip(String(v), 90)} />
        {spec.series.map((s, i) => (
          <Radar key={s.dataKey} name={s.name} dataKey={s.dataKey} stroke={pickColor(i, s.color)} fill={pickColor(i, s.color)} fillOpacity={0.2} isAnimationActive={false} />
        ))}
        <Tooltip wrapperStyle={{ outline: "none" }} content={<BrandTooltip format={(v) => formatNumberTick(v, spec.unit)} />} />
        {spec.series.length > 1 ? <Legend wrapperStyle={{ fontSize: 12, color: theme.textSecondary, paddingTop: 4 }} /> : null}
      </RadarChart>
    );
  }

  // ---- scatter: each series rendered as scatter points ----
  if (kind === "scatter") {
    return (
      <ScatterChart width={w} height={h} margin={{ top: 8, right: 12, left: 4, bottom: 8 }}>
        <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" strokeOpacity={0.85} />
        <XAxis
          type={typeof data[0]?.[xKey] === "number" ? "number" : "category"}
          dataKey={xKey}
          tick={axisTick}
          tickLine={false}
          axisLine={{ stroke: theme.border }}
          // The x of a scatter is its own measure, in its own unit.
          name={spec.xLabel || xKey}
          tickFormatter={(v) => (typeof v === "number" ? formatNumberTick(v, spec.xUnit !== undefined ? spec.xUnit : spec.unit) : formatXAxisTick(v))}
        />
        <YAxis
          tick={axisTick}
          tickLine={false}
          axisLine={{ stroke: theme.border }}
          width={48}
          tickFormatter={yTickFormatter(spec, data)}
        />
        <ZAxis range={[60, 60]} />
        <Tooltip cursor={{ strokeDasharray: "3 3", stroke: "var(--border-default)" }} wrapperStyle={{ outline: "none" }} content={<BrandTooltip format={(v, n) => formatNumberTick(v, n === (spec.xLabel || xKey) && spec.xUnit !== undefined ? spec.xUnit : spec.unit)} />} />
        <Legend wrapperStyle={{ fontSize: 12, color: theme.textSecondary, paddingTop: 4 }} />
        {spec.series.map((s, i) => (
          <Scatter
            key={s.dataKey}
            name={s.name}
            data={data.map((row) => ({ [xKey]: row[xKey], [s.dataKey]: row[s.dataKey] }))}
            dataKey={s.dataKey}
            fill={pickColor(i, s.color)}
          />
        ))}
      </ScatterChart>
    );
  }

  // ---- flow_paths: rendered by FlowPathsView outside ChartBody ----
  if (kind === "flow_paths") {
    return null;
  }

  // ---- retention_matrix: grid of cohort rows × day columns ----
  if (kind === "retention_matrix") {
    const dayKeys = spec.series.map((s) => s.dataKey);
    if (!dayKeys.length) return null;
    let max = 0;
    for (const row of data) {
      for (const dk of dayKeys) {
        const n = Number(row[dk]);
        if (Number.isFinite(n) && n > max) max = n;
      }
    }
    const baseColor = pickColor(0, spec.series[0].color);
    const cellGap = 3;
    const labelW = 80;
    const cols = dayKeys.length;
    const cellW = Math.max(28, Math.floor((w - labelW - 24 - cellGap * (cols - 1)) / Math.max(1, cols)));
    const cellH = Math.max(22, Math.min(36, Math.floor((h - 40) / Math.max(1, data.length))));
    return (
      <div style={{ width: w, height: h, padding: 8, overflow: "auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", marginBottom: 4, paddingLeft: labelW + 8 }}>
          {spec.series.map((s, i) => (
            <div
              key={`hdr-${i}`}
              style={{
                width: cellW,
                marginRight: i === cols - 1 ? 0 : cellGap,
                fontSize: 11,
                color: theme.textSecondary,
                textAlign: "center",
              }}
            >
              {s.name || s.dataKey}
            </div>
          ))}
        </div>
        {data.map((row, ri) => (
          <div key={`rm-${ri}`} style={{ display: "flex", alignItems: "center", marginBottom: cellGap }}>
            <div
              style={{
                width: labelW,
                fontSize: 11,
                color: theme.textSecondary,
                paddingRight: 8,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={String(row[xKey])}
            >
              {String(row[xKey])}
            </div>
            {dayKeys.map((dk, ci) => {
              const v = Number(row[dk]);
              const t = Number.isFinite(v) && max > 0 ? v / max : 0;
              const opacity = v === 0 ? 0.06 : 0.18 + 0.82 * t;
              return (
                <div
                  key={`rm-${ri}-${ci}`}
                  title={`${row[xKey]} • ${spec.series[ci].name || dk}: ${formatNumberTick(v, spec.unit)}`}
                  style={{
                    width: cellW,
                    height: cellH,
                    marginRight: ci === cols - 1 ? 0 : cellGap,
                    backgroundColor: baseColor,
                    opacity,
                    borderRadius: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    color: t > 0.5 ? "var(--surface-base)" : theme.textPrimary,
                    fontWeight: 500,
                  }}
                >
                  {Number.isFinite(v) ? formatNumberTick(v, spec.unit) : ""}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  // ---- heatmap: grid of cells coloured by series[0].dataKey value ----
  if (kind === "heatmap") {
    const valKey = spec.series[0]?.dataKey;
    if (!valKey) return null;
    const values = data.map((r) => Number(r[valKey])).filter((v) => Number.isFinite(v));
    const min = values.length ? Math.min(...values) : 0;
    const max = values.length ? Math.max(...values) : 1;
    const range = max - min || 1;
    const baseColor = pickColor(0, spec.series[0].color);
    const cellGap = 4;
    const cellW = Math.max(20, Math.floor((w - 24 - cellGap * (data.length - 1)) / Math.max(1, data.length)));
    const cellH = Math.max(28, Math.min(64, h - 48));
    return (
      <div style={{ width: w, height: h, padding: 8, overflow: "hidden", boxSizing: "border-box" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: cellGap, alignContent: "flex-start" }}>
          {data.map((row, i) => {
            const v = Number(row[valKey]);
            const t = Number.isFinite(v) ? (v - min) / range : 0;
            const opacity = 0.15 + 0.85 * t;
            return (
              <div
                key={`hm-${i}`}
                title={`${row[xKey]}: ${formatNumberTick(v, spec.unit)}`}
                style={{
                  width: cellW,
                  height: cellH,
                  backgroundColor: baseColor,
                  opacity,
                  borderRadius: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  color: theme.textPrimary,
                  fontWeight: 500,
                }}
              >
                {formatNumberTick(v, spec.unit)}
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 8, fontSize: 11, color: theme.textSecondary }}>
          {String(data[0]?.[xKey])} → {String(data[data.length - 1]?.[xKey])}
        </div>
      </div>
    );
  }

  // ---- sankey: how much flows from each left value to each right value ----
  if (kind === "sankey" && spec.yKey) {
    return <SankeyView spec={spec} width={w} height={h} />;
  }

  // ---- matrix: x × y cells, shaded by value ----
  if (kind === "matrix" && spec.yKey) {
    return <MatrixView spec={spec} width={w} height={h} />;
  }

  // ---- funnel: column chart (matches studio FunnelSummaryBarChart) ----
  // The studio's funnel page renders steps as a column bar chart, not a
  // cone-shape funnel widget. The chat follows the same convention so
  // funnel charts feel native to the product.
  if (kind === "funnel") {
    const stepKey = spec.series[0]?.dataKey;
    if (!stepKey) return null;
    const series = spec.series[0];
    const barColor = pickColor(0, series.color);
    return (
      <BarChart width={w} height={h} data={data} margin={{ top: 16, right: 12, left: 4, bottom: 8 }}>
        <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" strokeOpacity={0.85} vertical={false} />
        <XAxis
          dataKey={xKey}
          tick={axisTick}
          tickLine={false}
          axisLine={{ stroke: theme.border }}
          interval={0}
          tickFormatter={(v) => middleClip(String(v), Math.max(24, (w - 70) / Math.max(1, data.length) - 6))}
        />
        <YAxis
          tick={axisTick}
          tickLine={false}
          axisLine={{ stroke: theme.border }}
          width={48}
          tickFormatter={yTickFormatter(spec, data)}
        />
        <Tooltip cursor={HOVER_CURSOR} wrapperStyle={{ outline: "none" }} content={<BrandTooltip format={(v) => formatNumberTick(v, spec.unit)} />} />
        <Bar
          name={series.name || "Users"}
          dataKey={stepKey}
          fill={barColor}
          radius={[4, 4, 0, 0]}
          maxBarSize={64}
        >
          <LabelList
            dataKey={stepKey}
            position="top"
            fill={theme.textPrimary}
            fontSize={11}
            formatter={(v) => formatNumberTick(v, spec.unit)}
          />
        </Bar>
      </BarChart>
    );
  }

  // ---- shared cartesian header for bar / line / area ----
  const isTimeSeries = (kind === "line" || kind === "area" || kind === "combo" || kind === "bar") && looksLikeTimeSeriesXAxis(data, xKey);

  const common = (
    <>
      <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" strokeOpacity={0.85} />
      <XAxis
        dataKey={xKey}
        tick={axisTick}
        tickLine={false}
        axisLine={{ stroke: theme.border }}
        // ALWAYS formatted, not only for time series. This axis is the one
        // bar charts use, and `undefined` here let a 38-character model name
        // reach the SVG untouched — `Llama-4-Maverick-17B-128E-Instruct-FP8`
        // ran off the edge of a cost chart on production. formatXAxisTick
        // already returns a date label when the value is a date, so applying
        // it unconditionally costs the time-series path nothing.
        tickFormatter={formatXAxisTick}
        minTickGap={isTimeSeries ? 24 : 8}
      />
      <YAxis
        tick={axisTick}
        tickLine={false}
        axisLine={{ stroke: theme.border }}
        width={48}
        tickFormatter={yTickFormatter(spec, data)}
        {...(spec.percent ? { domain: [0, 100], ticks: [0, 25, 50, 75, 100], allowDataOverflow: true } : {})}
      />
      <Tooltip cursor={HOVER_CURSOR} wrapperStyle={{ outline: "none" }} content={<BrandTooltip format={(v) => formatNumberTick(v, spec.unit)} labelFormat={isTimeSeries ? formatXAxisTick : undefined} />} />
      {/* One series is named by the title; a legend would say it twice. */}
      {spec.series.length > 1 ? <Legend wrapperStyle={{ fontSize: 12, color: theme.textSecondary, paddingTop: 4 }} /> : null}
    </>
  );

  // LONG NAMES READ ACROSS, NOT UNDER. Eval and model names are 20–40
  // characters; under vertical bars every label was cut to "UI test · Sim…".
  // Bars laid sideways give each name the width it needs.
  const longest = kind === "bar" ? Math.max(0, ...data.map((r) => String(r[xKey] ?? "").length)) : 0;
  const stackId = spec.stacked ? "stack" : undefined;
  // Dates are long strings too, but a time axis reads left to right.
  const timeAxis = kind === "bar" && data.length > 0 && data.every((r) => formatPossibleDate(r[xKey]));
  if (kind === "bar" && (spec.horizontal || (!timeAxis && longest > 12 && data.length <= 20))) {
    const widest = Math.max(0, ...data.map((r) => textWidth(String(r[xKey] ?? ""))));
    const labelWidth = Math.min(Math.round(w * 0.45), Math.ceil(widest) + 10);
    const clip = (v) => middleClip(String(v ?? ""), labelWidth - 10);
    return (
      <BarChart layout="vertical" width={w} height={h} data={data} margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
        <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" strokeOpacity={0.85} horizontal={false} />
        <XAxis type="number" tick={axisTick} tickLine={false} axisLine={{ stroke: theme.border }} tickFormatter={yTickFormatter(spec, data)}
          {...(spec.percent ? { domain: [0, 100], ticks: [0, 25, 50, 75, 100] } : {})} />
        <YAxis type="category" dataKey={xKey} width={labelWidth} interval={0} tick={axisTick} tickLine={false}
          axisLine={{ stroke: theme.border }} tickFormatter={clip} />
        <Tooltip cursor={HOVER_CURSOR} wrapperStyle={{ outline: "none" }} content={<BrandTooltip format={(v) => formatNumberTick(v, spec.unit)} />} />
        {spec.series.length > 1 ? <Legend wrapperStyle={{ fontSize: 12, color: theme.textSecondary, paddingTop: 4 }} /> : null}
        {spec.series.map((s, i) => (
          <Bar key={s.dataKey} name={s.name} dataKey={s.dataKey} fill={pickColor(i, s.color)} stackId={stackId}
            radius={!stackId || i === spec.series.length - 1 ? [0, 4, 4, 0] : 0} maxBarSize={22} />
        ))}
      </BarChart>
    );
  }

  // ---- combo: the first number as columns, the others as lines on their own axis ----
  if (kind === "combo") {
    const [first, ...rest] = spec.series;
    return (
      <ComposedChart width={w} height={h} data={data} margin={{ top: 8, right: rest.length ? 4 : 12, left: 4, bottom: 8 }}>
        {common}
        {rest.length ? (
          <YAxis yAxisId="r" orientation="right" tick={axisTick} tickLine={false} axisLine={{ stroke: theme.border }} width={44}
            tickFormatter={(v) => formatNumberTick(v)} />
        ) : null}
        <Bar name={first.name} dataKey={first.dataKey} fill={pickColor(0, first.color)} radius={[4, 4, 0, 0]} maxBarSize={48} />
        {rest.map((s, i) => (
          // Straight between points: a curve across days with no runs would draw values nobody measured.
          <Line key={s.dataKey} yAxisId="r" type="linear" name={s.name} dataKey={s.dataKey} stroke={pickColor(i + 1, s.color)}
            strokeWidth={2} legendType="plainline" connectNulls activeDot={false}
            dot={(p) => (isolatedPoint(data, s.dataKey, p.index)
              ? <circle key={`${s.dataKey}-${p.index}`} cx={p.cx} cy={p.cy} r={3} fill={pickColor(i + 1, s.color)} />
              : <g key={`${s.dataKey}-${p.index}`} />)} />
        ))}
      </ComposedChart>
    );
  }

  if (kind === "bar") {
    return (
      <BarChart width={w} height={h} data={data} margin={{ top: 8, right: 12, left: 4, bottom: 8 }}>
        {common}
        {spec.series.map((s, i) => (
          <Bar
            key={s.dataKey}
            name={s.name}
            dataKey={s.dataKey}
            fill={pickColor(i, s.color)}
            stackId={stackId}
            radius={!stackId || i === spec.series.length - 1 ? [4, 4, 0, 0] : 0}
            maxBarSize={48}
          />
        ))}
      </BarChart>
    );
  }

  if (kind === "line") {
    return (
      <LineChart width={w} height={h} data={data} margin={{ top: 8, right: 12, left: 4, bottom: 8 }}>
        {common}
        {spec.series.map((s, i) => {
          const lineColor = pickColor(i, s.color);
          return (
            <Line
              key={s.dataKey}
              type="monotone"
              name={s.name}
              dataKey={s.dataKey}
              stroke={lineColor}
              strokeWidth={2}
              // The legend shows the line as it is drawn: a plain stroke,
              // not Recharts' default line-with-a-dot.
              legendType="plainline"
              // ONE LINE, NO HOLES. A bucket with nothing in it is 0 for a
              // count (the server fills it); for an average it has no value,
              // and the line runs straight on to the next one it has.
              connectNulls
              // A LINE, NOT A STRING OF BEADS. Every point carried a filled
              // marker and the hovered one turned bright white, so a 30-day
              // series read as thirty dots joined by a line and the cursor
              // punched a hole through it. The tooltip already says which
              // point you are on; the line should just be a line.
              // Except a point with no neighbour: a line needs two points,
              // so an eval that ran on one day drew nothing at all.
              dot={(p) => (isolatedPoint(data, s.dataKey, p.index)
                ? <circle key={`${s.dataKey}-${p.index}`} cx={p.cx} cy={p.cy} r={3} fill={lineColor} />
                : <g key={`${s.dataKey}-${p.index}`} />)}
              activeDot={false}
            />
          );
        })}
      </LineChart>
    );
  }

  // Default: area chart
  return (
    <AreaChart width={w} height={h} data={data} margin={{ top: 8, right: 12, left: 4, bottom: 8 }}>
      {common}
      {spec.series.map((s, i) => (
        <Area
          key={s.dataKey}
          type="monotone"
          name={s.name}
          dataKey={s.dataKey}
          stroke={pickColor(i, s.color)}
          fill={pickColor(i, s.color)}
          legendType="plainline"
          // A stacked line is a stacked area drawn as its edges only.
          fillOpacity={spec.lineOnly ? 0 : spec.stacked ? 0.35 : 0.22}
          strokeWidth={2}
          stackId={stackId}
          connectNulls
        />
      ))}
    </AreaChart>
  );
});

ChartBody.displayName = "ChartBody";

const MAX_SANKEY_LINKS = 60;
const MAX_SANKEY_SIDE = 14;

/** Whether edges between named nodes ever lead back to where they started. */
function hasCycle(edges) {
  const out = new Map();
  for (const [a, b] of edges) { if (!out.has(a)) out.set(a, []); out.get(a).push(b); }
  const state = new Map(); // 1 visiting, 2 done
  const visit = (n) => {
    if (state.get(n) === 1) return true;
    if (state.get(n) === 2) return false;
    state.set(n, 1);
    for (const m of out.get(n) || []) if (visit(m)) return true;
    state.set(n, 2);
    return false;
  };
  return [...out.keys()].some(visit);
}

/**
 * Source → target, one row per edge. When the edges chain (step 1 → step 2,
 * step 2 → step 3) without looping, a name is one node and the flow runs
 * through as many stages as it has; a flow that loops, or a self-edge, keeps
 * its two sides apart (a name on both sides is two nodes) so it can still be drawn.
 */
function SankeyView({ spec, width, height }) {
  const valueKey = spec.series[0]?.dataKey;
  const graph = useMemo(() => {
    const rows = spec.data.filter((r) => Number(r[valueKey]) > 0);
    // The long tail on either side is one "Other" node: forty tools are forty labels nobody can read.
    const fold = (idx) => {
      const totals = new Map();
      for (const r of spec.data) { const k = String(r[idx]); totals.set(k, (totals.get(k) || 0) + (Number(r[valueKey]) || 0)); }
      if (totals.size <= MAX_SANKEY_SIDE) return (k) => k;
      const keep = new Set([...totals.entries()].sort((a, b) => b[1] - a[1]).slice(0, MAX_SANKEY_SIDE - 1).map(([k]) => k));
      const other = `${totals.size - keep.size} other`;
      return (k) => (keep.has(k) ? k : other);
    };
    const fx = fold(spec.xKey);
    const fy = fold(spec.yKey);
    const merged = new Map();
    for (const r of rows) {
      const a = fx(String(r[spec.xKey]));
      const b = fy(String(r[spec.yKey]));
      const k = `${a}\u0000${b}`;
      merged.set(k, [a, b, (merged.get(k)?.[2] || 0) + Number(r[valueKey])]);
    }
    const pairs = [...merged.values()].sort((x, y) => y[2] - x[2]).slice(0, MAX_SANKEY_LINKS);
    const staged = pairs.every(([a, b]) => a !== b) && !hasCycle(pairs);
    const ids = new Map();
    const nodes = [];
    const nodeFor = (side, name) => {
      const k = staged ? name : `${side}:${name}`;
      if (!ids.has(k)) { ids.set(k, nodes.length); nodes.push({ name, side }); }
      return ids.get(k);
    };
    const links = pairs.map(([a, b, v]) => ({ source: nodeFor("l", a), target: nodeFor("r", b), value: v }));
    if (staged) {
      // A node's side is where its label goes: sources left, the rest right.
      const hasIn = new Set(links.map((l) => l.target));
      nodes.forEach((n, i) => { n.side = hasIn.has(i) ? "r" : "l"; });
      // Its colour is its stage, counted from the sources.
      const depth = new Array(nodes.length).fill(0);
      for (let pass = 0; pass < nodes.length; pass += 1) {
        let moved = false;
        for (const l of links) if (depth[l.target] < depth[l.source] + 1) { depth[l.target] = depth[l.source] + 1; moved = true; }
        if (!moved) break;
      }
      nodes.forEach((n, i) => { n.depth = depth[i]; });
    }
    return { nodes, links };
  }, [spec, valueKey]);
  if (!graph.links.length) return null;
  const labelWidth = Math.min(160, Math.max(60, Math.floor(width * 0.22)));
  const Node = ({ x, y, width: nw, height: nh, index, payload }) => {
    const n = graph.nodes[index] || {};
    const isLeft = n.side === "l";
    if (n.depth != null) {
      return (
        <g>
          <rect x={x} y={y} width={nw} height={Math.max(1, nh)} rx={2} fill={pickColor(n.depth)} />
          <text x={isLeft ? x - 6 : x + nw + 6} y={y + nh / 2} dy="0.35em" textAnchor={isLeft ? "end" : "start"}
            fontSize={11} fill={theme.textSecondary}>
            <title>{`${payload.name}: ${formatNumberTick(payload.value, spec.unit)}`}</title>
            {middleClip(String(payload.name), labelWidth - 8)}
          </text>
        </g>
      );
    }
    const label = middleClip(String(payload.name), labelWidth - 8);
    return (
      <g>
        <rect x={x} y={y} width={nw} height={Math.max(1, nh)} rx={2} fill={pickColor(isLeft ? 0 : 1)} />
        <text x={isLeft ? x - 6 : x + nw + 6} y={y + nh / 2} dy="0.35em" textAnchor={isLeft ? "end" : "start"}
          fontSize={11} fill={theme.textSecondary}>
          <title>{`${payload.name}: ${formatNumberTick(payload.value, spec.unit)}`}</title>
          {label}
        </text>
      </g>
    );
  };
  return (
    <Sankey
      width={width}
      height={height}
      data={graph}
      node={<Node />}
      nodePadding={10}
      nodeWidth={10}
      link={{ stroke: "var(--trodo-primary)", strokeOpacity: 0.25 }}
      margin={{ top: 8, bottom: 8, left: labelWidth, right: labelWidth }}
      iterations={32}
    >
      <Tooltip wrapperStyle={{ outline: "none" }} content={<BrandTooltip format={(v) => formatNumberTick(v, spec.unit)} />} />
    </Sankey>
  );
}

const MATRIX_MAX = 24;

/** Rows by the second grouping, columns by the first, each cell shaded by its value. */
/**
 * Daily date keys with the missing days put back, in the keys' own format
 * ("2026-10-15" or "2026-10-15T00:00:00.000Z"). A query grouped by day has
 * no row for a day with nothing in it, and the axis skipped from Oct 15 to
 * Oct 20. Anything that is not whole days comes back as it was.
 */
function everyDay(sorted) {
  const DAY = 86400000;
  const first = sorted[0];
  const isoMidnight = /^\d{4}-\d{2}-\d{2}T00:00:00(\.000)?Z$/;
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/;
  if (!sorted.every((k) => dateOnly.test(k) || isoMidnight.test(k))) return sorted;
  const a = Date.parse(dateOnly.test(first) ? `${first}T00:00:00Z` : first);
  const last = sorted[sorted.length - 1];
  const b = Date.parse(dateOnly.test(last) ? `${last}T00:00:00Z` : last);
  if (!(b > a) || (b - a) / DAY > 90) return sorted;
  const fmt = (ms) => {
    const iso = new Date(ms).toISOString();
    if (dateOnly.test(first)) return iso.slice(0, 10);
    return first.includes(".000") ? iso : iso.replace(".000", "");
  };
  const out = [];
  for (let ms = a; ms <= b; ms += DAY) out.push(fmt(ms));
  return out;
}

/** The height a heatmap needs for its rows, so a two-row grid is not a 260px card. */
function matrixHeight(spec) {
  const valueKey = spec.series?.[0]?.dataKey;
  const t = new Map();
  for (const r of spec.data || []) t.set(String(r[spec.yKey]), (t.get(String(r[spec.yKey])) || 0) + (Number(r[valueKey]) || 0));
  const rows = Math.min(MATRIX_MAX, [...t.values()].filter((v) => v !== 0).length || t.size || 1);
  return Math.max(120, Math.min(560, rows * 30 + 70));
}

function MatrixView({ spec, width, height }) {
  const valueKey = spec.series[0]?.dataKey;
  const valueName = spec.series[0]?.name || valueKey;
  const [hover, setHover] = useState(null);
  const { xs, ys, cell, max } = useMemo(() => {
    const totals = (key) => {
      const t = new Map();
      for (const r of spec.data) t.set(String(r[key]), (t.get(String(r[key])) || 0) + (Number(r[valueKey]) || 0));
      return t;
    };
    // A row or column with nothing in it says nothing: five agents with no
    // failures filled most of a failures grid with empty cells. Kept only
    // when every row (or column) is empty, so an all-zero grid still draws.
    const order = (key) => {
      const t = totals(key);
      const all = [...t.keys()];
      // Time keeps every step, empty or not: a gap in the days is a gap in
      // the reader's timeline, not noise.
      if (all.length && all.every((n) => formatPossibleDate(n))) return everyDay(all.sort()).slice(-MATRIX_MAX);
      const live = all.filter((n) => t.get(n) !== 0);
      const names = live.length ? live : all;
      return names.sort((a, b) => t.get(b) - t.get(a)).slice(0, MATRIX_MAX);
    };
    const c = new Map();
    let m = 0;
    for (const r of spec.data) {
      const v = Number(r[valueKey]);
      if (!Number.isFinite(v)) continue;
      const k = `${r[spec.xKey]}\u0000${r[spec.yKey]}`;
      c.set(k, (c.get(k) || 0) + v);
      m = Math.max(m, c.get(k));
    }
    return { xs: order(spec.xKey), ys: order(spec.yKey), cell: c, max: m };
  }, [spec, valueKey]);
  // Row labels get the room their names need, up to a third of the width.
  const longest = Math.max(0, ...ys.map((y) => String(y).length));
  const labelW = Math.min(Math.floor(width * 0.33), 150, Math.max(56, Math.round(longest * 6.6) + 12));
  // The grid fits the card instead of scrolling sideways.
  const gap = 2;
  const cellW = Math.max(8, Math.floor((width - labelW - 24 - gap * xs.length) / Math.max(1, xs.length)));
  const legendH = 26;
  const cellH = Math.max(16, Math.min(34, Math.floor((height - 34 - legendH) / Math.max(1, ys.length))));
  const showValues = cellW >= 44 && cellH >= 20;
  // Column labels as dates a person reads ("Oct 15"), thinned to every few
  // columns when the cells are narrow; never a bare "15" or a clipped "O…".
  const every = Math.max(1, Math.ceil(48 / Math.max(1, cellW + gap)));
  const xLabel = (v, i) => {
    if (i % every !== 0) return "";
    const full = formatPossibleDate(v) || String(v);
    return middleClip(full, Math.max(cellW, (cellW + gap) * every - 4));
  };
  const fill = (t) => `color-mix(in srgb, var(--trodo-primary) ${Math.round(12 + 88 * t)}%, var(--surface-base))`;
  const fmt = (v) => formatNumberTick(v, spec.unit, max);
  return (
    <div data-matrix style={{ width: width - 16, height, overflow: "hidden", fontSize: 11, color: theme.textSecondary, position: "relative" }}>
      <div style={{ display: "grid", gridTemplateColumns: `${labelW}px repeat(${xs.length}, ${cellW}px)`, gap }}>
        {ys.map((y) => (
          <React.Fragment key={`r-${y}`}>
            <div title={y} style={{ height: cellH, lineHeight: `${cellH}px`, textAlign: "right", paddingRight: 6, whiteSpace: "nowrap", overflow: "hidden" }}>
              {middleClip(String(y), labelW - 10)}
            </div>
            {xs.map((x) => {
              const v = cell.get(`${x}\u0000${y}`);
              const t = v != null && max > 0 ? v / max : 0;
              return (
                <div key={`c-${x}-${y}`}
                  onMouseEnter={(e) => {
                    const box = e.currentTarget.getBoundingClientRect();
                    const root = e.currentTarget.closest("[data-matrix]").getBoundingClientRect();
                    setHover({ x, y, v, left: box.left - root.left + box.width / 2, top: box.top - root.top });
                  }}
                  onMouseLeave={() => setHover(null)}
                  style={{
                    height: cellH, borderRadius: 3, cursor: "default",
                    background: v == null ? "var(--surface-sunken)" : fill(t),
                    outline: hover && hover.x === x && hover.y === y ? `1px solid ${theme.textPrimary}` : "none",
                    color: t > 0.55 ? "var(--text-on-brand)" : theme.textPrimary,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontVariantNumeric: "tabular-nums",
                  }}>
                  {showValues && v != null ? fmt(v) : null}
                </div>
              );
            })}
          </React.Fragment>
        ))}
        <div />
        {xs.map((x, i) => (
          <div key={`x-${x}`} style={{ textAlign: "left", whiteSpace: "nowrap", overflow: "visible", paddingTop: 2 }}>{xLabel(x, i)}</div>
        ))}
      </div>
      {hover && (
        <div style={{
          position: "absolute", pointerEvents: "none", zIndex: 2, whiteSpace: "nowrap",
          left: Math.min(Math.max(hover.left, 70), width - 86), // Above the cell, or below it on the top rows where above is off the card.
          ...(hover.top < 48 ? { top: hover.top + cellH + 6, transform: "translateX(-50%)" } : { top: hover.top - 6, transform: "translate(-50%, -100%)" }),
          background: theme.bgRaised, border: `1px solid ${theme.border}`, borderRadius: 8, padding: "6px 8px",
          fontSize: 12, color: theme.textPrimary, boxShadow: "var(--shadow-lg)",
        }}>
          <div style={{ color: theme.textSecondary }}>{formatPossibleDate(hover.x) || hover.x} · {hover.y}</div>
          <div>{valueName}: <strong>{hover.v == null ? "no data" : fmt(hover.v)}</strong></div>
        </div>
      )}
      {/* What the colours mean. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, paddingLeft: labelW }}>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>0</span>
        <span style={{ width: 90, height: 8, borderRadius: 4, background: `linear-gradient(90deg, ${fill(0)}, ${fill(1)})` }} />
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(max)}</span>
        <span style={{ marginLeft: 4 }}>{valueName}</span>
      </div>
    </div>
  );
}

function useMeasuredChartSize() {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 400, height: 240 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    let rafId = 0;
    const read = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const cw = el.clientWidth;
        const ch = el.clientHeight;
        const w = cw > 8 ? Math.max(200, Math.floor(cw)) : 400;
        const h = ch > 8 ? Math.max(120, Math.floor(ch)) : 240;
        setSize((prev) => {
          if (Math.abs(prev.width - w) < 2 && Math.abs(prev.height - h) < 2) return prev;
          return { width: w, height: h };
        });
      });
    };

    read();
    const ro = new ResizeObserver(() => read());
    ro.observe(el);
    window.addEventListener("resize", read);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
      window.removeEventListener("resize", read);
    };
  }, []);

  return [ref, size];
}

// ---------------------------------------------------------------------------
// SpecTableView — derives a markdown-equivalent table from the chart spec.
// Used by the Table tab so users can read the same numbers without scrolling
// to a separate markdown table.
// ---------------------------------------------------------------------------

function formatCellValue(v) {
  if (v == null) return "";
  if (typeof v === "number" && Number.isFinite(v)) {
    if (Number.isInteger(v)) return v.toLocaleString();
    return v.toFixed(2).replace(/\.?0+$/, "");
  }
  return String(v);
}

function SpecTableView({ spec }) {
  const { xKey, series, data } = spec;
  const headers = [xKey, ...series.map((s) => s.name || s.dataKey)];
  // An hourly series is 200+ rows: 25 at a time, like every chat table.
  const paged = usePaged(data);
  return (
    <div className="px-3 pb-3">
      <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
        <div className="overflow-auto" style={{ maxHeight: 320 }}>
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className={tableHeaderRowClass}>
                {headers.map((h, i) => (
                  <th key={`th-${i}`} className={tableHeaderCellClass}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.visible.map((row, ri) => (
                <tr key={`tr-${paged.page}-${ri}`} className={STATIC_ROW_CLASS}>
                  <td className={`${tableDataCellClass} whitespace-nowrap`}>
                    {/* The x value as every chat table shows it: a day reads "Oct 14", never an ISO string. */}
                    {cellDisplay(row[xKey], typeof row[xKey] === "string" && /^\d{4}-\d{2}-\d{2}T/.test(row[xKey]) ? "date" : null, xKey)}
                  </td>
                  {series.map((s) => (
                    <td
                      key={`td-${ri}-${s.dataKey}`}
                      className={`${tableDataCellClass} whitespace-nowrap tabular-nums`}
                    >
                      {formatCellValue(row[s.dataKey])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <ChatPager paged={paged} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// FlowPathsView — card-based visualization for flow paths (replaces Sankey).
// Each path is a horizontal chain of event nodes with counts and a volume bar.
// ---------------------------------------------------------------------------

function FlowPathsView({ spec }) {
  const paths = spec.data || [];
  if (!paths.length) {
    return (
      <p className="text-sm py-6 text-center" style={{ color: theme.textMuted }}>
        No paths found.
      </p>
    );
  }
  const maxUsers = paths[0]?.users || 1;

  return (
    <div style={{ padding: "8px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
      {paths.map((path, pi) => {
        const events = path.events || [];
        const counts = path.counts || [];
        const users = path.users || 0;
        const pct = path.conversion_pct ?? path.conversionPct ?? 0;
        const barWidth = maxUsers > 0 ? Math.max(4, (users / maxUsers) * 100) : 4;
        return (
          <div
            key={`fp-${pi}`}
            style={{
              border: `1px solid ${theme.border}`,
              borderRadius: 10,
              padding: "10px 14px",
              backgroundColor: theme.bgSurface || "transparent",
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: theme.textSecondary }}>
                #{pi + 1}
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, color: theme.textPrimary }}>
                {formatNumberTick(users)} users
                {pct > 0 && (
                  <span style={{ fontWeight: 400, color: theme.textSecondary, marginLeft: 4, fontSize: 11 }}>
                    ({pct}%)
                  </span>
                )}
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4, marginBottom: 8 }}>
              {events.map((ev, ei) => (
                <React.Fragment key={`ev-${pi}-${ei}`}>
                  <div
                    style={{
                      display: "inline-flex",
                      flexDirection: "column",
                      alignItems: "center",
                      padding: "3px 8px",
                      borderRadius: 6,
                      backgroundColor: ei === 0 || ei === events.length - 1
                        ? (theme.primaryFaint || "var(--brand-primary-soft)")
                        : theme.bgRaised,
                      border: `1px solid ${ei === 0 || ei === events.length - 1 ? theme.primary : theme.border}`,
                    }}
                  >
                    <span style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: theme.textPrimary,
                      maxWidth: 140,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={ev}
                    >
                      {ev.length > 22 ? ev.slice(0, 20) + "\u2026" : ev}
                    </span>
                    {counts[ei] != null && (
                      <span style={{ fontSize: 10, color: theme.textSecondary }}>
                        {formatNumberTick(counts[ei])}
                      </span>
                    )}
                  </div>
                  {ei < events.length - 1 && (
                    <span style={{ color: theme.textMuted, fontSize: 12, lineHeight: 1 }}>&rarr;</span>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div style={{
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.borderMuted || theme.border,
              overflow: "hidden",
            }}>
              <div style={{
                height: "100%",
                width: `${barWidth}%`,
                borderRadius: 2,
                backgroundColor: theme.primary,
                transition: "width 0.3s ease",
              }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function chartInstanceKey(spec) {
  return [
    spec.title,
    spec.kind,
    spec.xKey,
    spec.series.map((s) => s.dataKey).join(","),
    spec.data.length,
  ].join("|");
}

class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <p className="text-sm py-6 text-center" style={{ color: theme.textMuted }}>
          Chart unavailable
        </p>
      );
    }
    return this.props.children;
  }
}

function trodoChartPropsEqual(prev, next) {
  if (prev === next) return true;
  try {
    return prev.bare === next.bare && JSON.stringify(prev.spec) === JSON.stringify(next.spec);
  } catch {
    return false;
  }
}

/**
 * Inline Recharts visualization for a validated chart spec (see `tryParseTrodoChartFence`).
 * @param {{ spec: object, bare?: boolean }} props
 */
function ChatInlineChartInner({ spec, bare = false }) {
  const [chartBoxRef, chartSize] = useMeasuredChartSize();
  const [view, setView] = useState("chart");

  const built = useMemo(() => buildRechartsReadySpec(spec), [spec]);
  // A partial/invalid spec (e.g. a single-row chart mid-stream) simply
  // doesn't render — no console noise; it resolves once the data is complete.
  if (!built.ok) return null;

  const rechartsSpec = built.spec;

  // `bare`: just the chart, filling its parent — a dashboard card draws its
  // own frame and title, and its height is the grid's, not a fixed 260px.
  if (bare) {
    return (
      <div ref={chartBoxRef} className="h-full w-full min-w-0" style={{ position: "relative", contain: "layout" }}>
        <ChartErrorBoundary key={chartInstanceKey(rechartsSpec)}>
          <ChartBody spec={rechartsSpec} width={chartSize.width} height={chartSize.height} />
        </ChartErrorBoundary>
      </div>
    );
  }
  const isFlowPaths = rechartsSpec.kind === "flow_paths";

  const tabBtnStyle = (active) => ({
    padding: "4px 10px",
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 6,
    border: `1px solid ${active ? theme.primary : theme.border}`,
    backgroundColor: active ? theme.primary : "transparent",
    color: active ? "var(--surface-base)" : theme.textSecondary,
    cursor: "pointer",
    transition: "all 0.15s ease",
  });

  return (
    <div
      className="chat-inline-chart w-full min-w-0 max-w-full rounded-xl border mt-4"
      style={{ borderColor: theme.border, backgroundColor: "transparent", contain: "layout paint" }}
    >
      <div className="flex items-center justify-between px-3 pt-2.5 pb-1.5 gap-3">
        <div className="truncate" style={BLOCK_TITLE_STYLE}>
          {rechartsSpec.title || ""}
        </div>
        {!isFlowPaths && (
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => setView("chart")}
              style={tabBtnStyle(view === "chart")}
              aria-pressed={view === "chart"}
            >
              Chart
            </button>
            <button
              type="button"
              onClick={() => setView("table")}
              style={tabBtnStyle(view === "table")}
              aria-pressed={view === "table"}
            >
              Table
            </button>
          </div>
        )}
      </div>
      {isFlowPaths ? (
        <FlowPathsView spec={rechartsSpec} />
      ) : view === "chart" ? (
        <div
          ref={chartBoxRef}
          className="h-[260px] max-[639px]:h-[200px] w-full min-w-[200px] max-w-full px-2 pb-2.5 box-border shrink-0"
          style={{
            position: "relative", contain: "layout",
            ...(rechartsSpec.kind === "matrix" && rechartsSpec.yKey ? { height: matrixHeight(rechartsSpec) } : {}),
          }}
        >
          <ChartErrorBoundary key={chartInstanceKey(rechartsSpec)}>
            <ChartBody spec={rechartsSpec} width={chartSize.width} height={chartSize.height} />
          </ChartErrorBoundary>
        </div>
      ) : (
        <SpecTableView spec={rechartsSpec} />
      )}
    </div>
  );
}

const ChatInlineChart = memo(ChatInlineChartInner, trodoChartPropsEqual);
ChatInlineChart.displayName = "ChatInlineChart";

export default ChatInlineChart;

// Exported for unit testing.
export const __test_only__ = {
  formatNumberTick,
  yTickFormatter,
  formatXAxisTick,
  formatPossibleDate,
  looksLikeTimeSeriesXAxis,
};
